import { Col, Row } from "react-grid-system";
import Container from "../common/Container";
import styles from "./CardActivation.module.css";
import StyledButton from "../common/buttons/StyledButton";
import { toolTypeToName } from "../AccountDetails/utils";
import { ToolType } from "../AccountDetails";
import classNames from "classnames";
import {
  useGetCardActivationInfo,
  useSubmitCardActivation,
} from "../../lib/api/queries";
import Spinner from "../common/Spinner";
import StyledError from "../common/StyledError";
import CardActivationInfo from "./CardActivationInfo";
import CardActivationScript from "./CardActivationScript";
import { ChannelMapToFull } from "../../lib/channelUtil";
import { useChannelState } from "../../lib/channelContext";

const strings = {
  activate: "Activate",
  close: "Close",
  errorTitle: "Failed to load Card Activation",
  successMessage: "Successfully submitted Card Activation",
};

type Props = {
  className?: string;
  dwbuid: string;
  toolId: string;
  onRequestClose: () => void;
  onDisplayGeneralErrors: (error: unknown) => void;
  onSetToolInProgress: (id: string, inProgress: boolean) => void;
  onSuccess: (summaryWithoutTime: string) => void;
  referenceId?: string;
};

export default function CardActivation({
  className,
  dwbuid,
  toolId,
  onRequestClose,
  onDisplayGeneralErrors,
  onSetToolInProgress,
  onSuccess,
  referenceId,
}: Props) {
  const { channel } = useChannelState();

  const getCardActivationInfo = useGetCardActivationInfo(dwbuid);
  const submitCardActivation = useSubmitCardActivation();

  const onSubmit = () => {
    if (!getCardActivationInfo.data) {
      return;
    }

    onSetToolInProgress(toolId, true);

    submitCardActivation
      .mutateAsync({
        dwbuid,
        accountOpenLongerThanSixMonths:
          getCardActivationInfo.data.accountOpenLongerThanSixMonths,
        referenceId,
        channel: ChannelMapToFull[channel],
      })
      .then(() => {
        onSetToolInProgress(toolId, false);
      })
      .catch(onDisplayGeneralErrors);
  };

  const onClose = () => {
    if (submitCardActivation.isSuccess) {
      onSuccess(strings.successMessage);
    } else {
      onRequestClose();
    }
  };

  return (
    <div className={classNames(styles.mainContainer, className)} id={toolId}>
      <Container>
        <Row>
          <Col md={2}>
            <h2 className={styles.header}>
              {toolTypeToName(ToolType.CardActivation)}
            </h2>
          </Col>
          <Col offset={{ md: 7 }} md={2}>
            {getCardActivationInfo.data?.showFields && (
              <StyledButton
                className={styles.wFull}
                onClick={onSubmit}
                isLoading={submitCardActivation.isLoading}
              >
                {strings.activate}
              </StyledButton>
            )}
          </Col>
          <Col md={1}>
            <StyledButton
              variant="secondary"
              className={styles.wFull}
              onClick={onClose}
            >
              {strings.close}
            </StyledButton>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className={styles.divider} />
          </Col>
        </Row>
        {getCardActivationInfo.isLoading ? (
          <Row>
            <Col>
              <Spinner />
            </Col>
          </Row>
        ) : getCardActivationInfo.isError ? (
          <Row>
            <Col>
              <StyledError
                errorTitle={strings.errorTitle}
                error={getCardActivationInfo.error}
                refetch={getCardActivationInfo.refetch}
              />
            </Col>
          </Row>
        ) : (
          <>
            <Row>
              <Col md={4}>
                <CardActivationInfo
                  cardActivationInfo={getCardActivationInfo.data}
                />
              </Col>
              <Col md={8}>
                <CardActivationScript
                  scriptName={
                    submitCardActivation.data?.scriptName ||
                    getCardActivationInfo.data.scriptName ||
                    undefined
                  }
                  cardActivationInfo={getCardActivationInfo.data}
                />
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
}
